/**
 * 运营大屏配置管理API
 * 提供飞机配置、数据录入历史的CRUD操作
 */

import apiClient from "./axios";

// ==================== 飞机配置相关API ====================

export const screenApi = {
    // 查询所有飞机配置
    getAircraftConfigs: () => apiClient.get("/v1/operation/screen/aircraft-configs"),

    // 根据飞机注册号查询配置
    getAircraftConfig: (aircraftRegistration) => apiClient.get(`/v1/operation/screen/aircraft-configs/${aircraftRegistration}`),

    // 创建飞机配置
    createAircraftConfig: (data) => apiClient.post("/v1/operation/screen/aircraft-configs", data),

    // 更新飞机配置
    updateAircraftConfig: ({ aircraftRegistration, data }) => apiClient.put(`/v1/operation/screen/aircraft-configs/${aircraftRegistration}`, data),

    // 删除飞机配置
    deleteAircraftConfig: (aircraftRegistration) => apiClient.delete(`/v1/operation/screen/aircraft-configs/${aircraftRegistration}`),

    // ==================== 数据录入历史相关API ====================

    // 查询数据录入历史
    getDataEntryHistory: (params = {}) => apiClient.get("/v1/operation/screen/history", { params }),

    // ==================== 飞行时间轴飞机配置相关API ====================

    // 查询所有时间轴飞机配置
    getTimelineAircraft: () => apiClient.get("/v1/operation/screen/timeline-aircraft"),

    // 添加时间轴飞机配置
    createTimelineAircraft: (data) => apiClient.post("/v1/operation/screen/timeline-aircraft", data),

    // 更新时间轴飞机配置
    updateTimelineAircraft: ({ aircraftRegistration, data }) => apiClient.put(`/v1/operation/screen/timeline-aircraft/${aircraftRegistration}`, data),

    // 删除时间轴飞机配置
    deleteTimelineAircraft: (aircraftRegistration) => apiClient.delete(`/v1/operation/screen/timeline-aircraft/${aircraftRegistration}`),

    // 批量更新时间轴飞机顺序
    reorderTimelineAircraft: (data) => apiClient.put("/v1/operation/screen/timeline-aircraft-reorder", data),

    // ==================== WebSocket连接监控相关API ====================

    // 获取WebSocket连接统计
    getWebSocketStats: () => apiClient.get("/v1/operation/screen/ws/stats"),

    // 获取展示页客户端状态
    getDisplayClientsStatus: () => apiClient.get("/v1/operation/screen/display-clients/status"),

    // ==================== 展示页数据聚合API ====================

    /**
     * 获取机队技术状态监控数据
     * 聚合了飞机配置（计划数据）和机队实际运行数据
     * @returns {Promise} 返回格式：{ items: [{aircraft_registration, planned_hours, actual_hours, monthly_planned_flights, monthly_executed_flights, monthly_on_time_rate, next_inspection_date}], total }
     */
    getFleetTechnicalStatus: () => apiClient.get("/v1/operation/screen/display/fleet-status"),

    /**
     * 获取飞行进程时间轴数据
     * 返回未来3天（今天、明天、后天）每架飞机的航班任务
     * @param {Object} params - 查询参数
     * @param {string} params.start_time - 开始时间（UTC，YYYY-MM-DDTHH:mm:ssZ）
     * @param {string} params.end_time - 结束时间（UTC，YYYY-MM-DDTHH:mm:ssZ）
     * @returns {Promise} 返回格式：{ items: [{aircraft_registration, flights: [{flight_number, route, std, sta, etd, eta, atd, ata, status}]}], total }
     */
    getFlightTimeline: (params = {}) => apiClient.get("/v1/operation/screen/display/flight-timeline", { params }),

    /**
     * 获取航班实时轨迹地图数据
     * @param {Object} params - 查询参数
     * @param {string} params.date - 查询日期（YYYY-MM-DD），默认今天
     * @returns {Promise} 返回格式：{ timestamp, date, aircraft: [{registration, aircraft_type, icao24, color, flights: [...]}] }
     */
    getFlightMap: (params = {}) => apiClient.get("/v1/operation/screen/display/flight-map", { params }),

    /**
     * 获取飞机历史轨迹点序列（用于回放）
     * @param {string} icao24 - 飞机 ICAO24 地址
     * @param {string} start - 开始时间（ISO8601）
     * @param {string} end - 结束时间（ISO8601）
     * @returns {Promise} { icao24, total_points, points: [{recorded_at, lat, lon, alt, heading, ...}] }
     */
    getFlightTrackHistory: (icao24, start, end) =>
        apiClient.get(`/v1/opensky/flight/${icao24}/history`, { params: { start, end } }),

    /**
     * 获取 rotation 航段链（用于整段回放）
     * @param {string} rotationNo - Rotation 编号
     * @param {string} aircraftReg - 飞机注册号
     */
    getRotationLegs: (rotationNo, aircraftReg) =>
        apiClient.get("/v1/operation/screen/display/rotation-legs", {
            params: { rotation_no: rotationNo, aircraft_reg: aircraftReg },
        }),

    /**
     * 获取ULD站点状态统计
     * 返回主要机场站点的在库数量统计
     * @param {Object} params - 查询参数
     * @param {string} params.station_codes - 站点代码（逗号分隔，如"HKG,LGG,HGH"）
     * @returns {Promise} 返回格式：{ items: [{station_code, station_name, available_count, threshold}], total }
     */
    getUldStationStats: (params = {}) => apiClient.get("/v1/operation/screen/display/uld-station-stats", { params }),

    // ==================== 右侧大屏相关API ====================

    /**
     * 获取所有机场的NOTAM数据
     * @returns {Promise} 返回格式：{ data: { LHBP: [...], EBLG: [...], ... } }
     */
    getAllNotams: () => apiClient.get("/v1/operation/screen/display/notams"),

    /**
     * 获取指定机场的NOTAM数据
     * @param {string} icaoCode - 机场ICAO代码 (如 LHBP, EBLG, ZSHC, VHHH, UATT, UACC)
     * @returns {Promise} 返回格式：{ icao_code, notams: [...], total }
     */
    getNotamsByIcao: (icaoCode) => apiClient.get(`/v1/operation/screen/display/notams/${icaoCode}`),

    /**
     * 获取右侧大屏航站综合信息
     * 包含ULD库存和NOTAM数据
     * @param {Object} params - 查询参数
     * @param {string} params.station_codes - 站点IATA代码，逗号分隔 (如 BUD,LGG,HGH,HKG,AKX,NQZ)
     * @returns {Promise} 返回格式：{ items: [{iata_code, icao_code, station_name, uld_count, notam_count, notams: [...]}], total }
     */
    getRightPanelStationInfo: (params = {}) => apiClient.get("/v1/operation/screen/display/right-panel/station-info", { params }),

    // ==================== 气象数据API ====================

    /**
     * 获取METAR气象观测数据
     * @param {string} ids - 机场ICAO代码，逗号分隔 (如 LHBP,EBLG,ZSHC)
     * @returns {Promise} 返回METAR数据数组
     */
    getMetar: (ids) => apiClient.get("/v1/operation/screen/display/weather/metar", { params: { ids } }),

    /**
     * 获取TAF气象预报数据
     * @param {string} ids - 机场ICAO代码，逗号分隔 (如 LHBP,EBLG,ZSHC)
     * @returns {Promise} 返回TAF数据数组
     */
    getTaf: (ids) => apiClient.get("/v1/operation/screen/display/weather/taf", { params: { ids } }),

    // ==================== 定检维护事件相关API ====================

    /**
     * 查询定检维护事件列表
     * @param {Object} params - 查询参数
     * @param {string} params.aircraft_registration - 可选，飞机注册号筛选
     * @returns {Promise} 返回维护事件列表
     */
    getMaintenanceEvents: (params = {}) => apiClient.get("/v1/operation/screen/maintenance-events", { params }),

    /**
     * 创建定检维护事件
     * @param {Object} data - 事件数据
     * @returns {Promise} 返回创建结果
     */
    createMaintenanceEvent: (data) => apiClient.post("/v1/operation/screen/maintenance-events", data),

    /**
     * 更新定检维护事件
     * @param {Object} params - 更新参数
     * @param {number} params.id - 事件ID
     * @param {Object} params.data - 更新数据
     * @returns {Promise} 返回更新结果
     */
    updateMaintenanceEvent: ({ id, data }) => apiClient.put(`/v1/operation/screen/maintenance-events/${id}`, data),

    /**
     * 删除定检维护事件
     * @param {number} id - 事件ID
     * @returns {Promise} 返回删除结果
     */
    deleteMaintenanceEvent: (id) => apiClient.delete(`/v1/operation/screen/maintenance-events/${id}`),

    /**
     * 获取展示页的维护事件数据（用于时间轴渲染）
     * @param {Object} params - 查询参数
     * @param {string} params.aircraft_registrations - 飞机注册号，逗号分隔
     * @param {string} params.start_date - 开始日期 (YYYY-MM-DD)
     * @param {string} params.end_date - 结束日期 (YYYY-MM-DD)
     * @returns {Promise} 返回按飞机分组的维护事件
     */
    getDisplayMaintenanceEvents: (params = {}) => apiClient.get("/v1/operation/screen/display/maintenance-events", { params }),
};

export default screenApi;


